import { differenceInCalendarDays, parseISO, format } from "date-fns";
import { Lock } from "lucide-react";

const STATUS_MAP = {
  not_started: { label: "Not started", cls: "bg-[#F1F5F9] text-[#94A3B8] border-[#E2E8F0]" },
  in_progress: { label: "In progress", cls: "bg-[#EEF2F7] text-[#475569] border-[#DDE3EC]" },
  completed: { label: "Protected", cls: "bg-amber-soft text-amber-ink border-amber-border" },
};

const DOC_MAP = {
  move_in: { label: "Move-In", cls: "bg-[#F1F5F9] text-[#475569] border-[#E2E8F0]" },
  move_out: { label: "Move-Out", cls: "bg-[#FEF3C7] text-[#92400E] border-[#FDE68A]" },
};

// documented_stage (onboarding fork) overrides the raw doc_type label on badges.
const STAGE_MAP = {
  moving_in: { label: "Move-in baseline", cls: "bg-[#F1F5F9] text-[#475569] border-[#E2E8F0]" },
  mid_lease: { label: "Late baseline", cls: "bg-[#E0E7FF] text-[#3730A3] border-[#C7D2FE]" },
  moving_out: { label: "Move-out documentation", cls: "bg-[#FEF3C7] text-[#92400E] border-[#FDE68A]" },
};

// "Protected" must only show once documentation is LOCKED. Zero photos → Not started;
// some photos, unlocked → In progress; locked → Protected. Pass isLocked + photoCount to derive.
export function StatusBadge({ status, isLocked, photoCount, testId }) {
  let key = status;
  if (isLocked !== undefined || photoCount !== undefined) {
    key = isLocked ? "completed" : ((photoCount || 0) > 0 ? "in_progress" : "not_started");
  }
  const m = STATUS_MAP[key] || STATUS_MAP.in_progress;
  return (
    <span data-testid={testId} className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-semibold border ${m.cls}`}>
      {m.label}
    </span>
  );
}

export function DocTypeBadge({ docType, stage, testId }) {
  const m = (stage && STAGE_MAP[stage]) || DOC_MAP[docType] || DOC_MAP.move_in;
  return (
    <span data-testid={testId} className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-semibold border ${m.cls}`}>
      {m.label}
    </span>
  );
}

const COND_MAP = {
  good: { label: "Good", cls: "bg-[#D1FAE5] text-[#177245] border-[#A7F3D0]" },
  fair: { label: "Fair", cls: "bg-[#FEF3C7] text-[#92400E] border-[#FDE68A]" },
  damaged: { label: "Damaged", cls: "bg-[#FEE2E2] text-[#B91C1C] border-[#FECACA]" },
};

export function ConditionBadge({ condition, testId }) {
  const m = COND_MAP[condition] || COND_MAP.fair;
  return (
    <span data-testid={testId} className={`inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-semibold border ${m.cls}`}>
      {m.label}
    </span>
  );
}

export function DeadlineCountdown({ deadline }) {
  if (!deadline) return <span className="text-xs text-[#94A3B8]">No deadline set</span>;
  const days = differenceInCalendarDays(parseISO(deadline), new Date());
  let cls = "text-[#177245]";
  let text = `${days} days left`;
  if (days < 0) { cls = "text-[#B91C1C]"; text = `${Math.abs(days)} days overdue`; }
  else if (days <= 7) cls = "text-[#92400E]";
  return (
    <span className={`text-xs font-semibold ${cls}`} data-testid="deadline-countdown">
      {text} · {format(parseISO(deadline), "MMM d")}
    </span>
  );
}


export function SealedStamp({ lockedAt, testId = "sealed-stamp" }) {
  let dateStr = "";
  try { dateStr = lockedAt ? format(parseISO(lockedAt), "MMM d, yyyy") : ""; } catch { dateStr = ""; }
  return (
    <div
      data-testid={testId}
      className="dd-stamp inline-flex items-center gap-2 rounded-lg px-3 py-1.5 font-mono select-none"
      title={`Documentation sealed ${dateStr}`}
    >
      <Lock size={15} className="shrink-0" />
      <div className="leading-none text-left">
        <p className="text-[10px] font-bold uppercase tracking-[0.14em]">Sealed</p>
        {dateStr && <p className="text-[10px] mt-0.5 tracking-wide opacity-80">{dateStr}</p>}
      </div>
    </div>
  );
}
